// src/services/googleDriveService.js
import { setIsLoading } from '../app/Actions/cmsAction';

// Load Google Drive API scripts dynamically
export const loadGapiScript = () => {
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[src="https://apis.google.com/js/api.js"]')) {
      if (window.gapi) {
        resolve(window.gapi);
        return;
      }
    }
    
    const script = document.createElement("script");
    script.src = "https://apis.google.com/js/api.js";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.gapi);
    script.onerror = () => reject(new Error('Failed to load Google API client'));
    document.body.appendChild(script);
  });
};

export const loadGsiScript = () => {
  return new Promise((resolve, reject) => {
    if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
      if (window.google && window.google.accounts) {
        resolve();
        return;
      }
    }
    
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.body.appendChild(script);
  });
};

// Configuration for Google Drive API
const GOOGLE_API_CONFIG = {
  // Replace with your own API key and client ID
  API_KEY: "AIzaSyAsXgj6GjjPee45sqv7XoVz4mSE2a7yECk",
  CLIENT_ID: "700872560029-qt7cq245nq6m7lp8ctld8ijk6k4502j6.apps.googleusercontent.com",
  DISCOVERY_DOCS: ["https://www.googleapis.com/discovery/v1/apis/drive/v3/rest"],
  SCOPES: "https://www.googleapis.com/auth/drive.file"
};

// Initialize the Google API client and Google Drive Picker
export const useGoogleDrivePicker = (dispatch) => {
  const openPicker = async ({ onSelect }) => {
    try {
      dispatch(setIsLoading(true));
      
      // Load required scripts
      await Promise.all([,loadGsiScript()]);
      
      // Initialize gapi client
      await new Promise((resolve, reject) => {
        window.gapi.load("client:picker", {
          callback: async () => {
            try {
              await window.gapi.client.init({
                apiKey: GOOGLE_API_CONFIG.API_KEY,
                discoveryDocs: GOOGLE_API_CONFIG.DISCOVERY_DOCS
              });
              resolve();
            } catch (error) {
              console.error("Error initializing gapi client:", error);
              reject(error);
            }
          },
          onerror: (error) => reject(error)
        });
      });
      
      // Initialize Google Identity Services and open the picker
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_API_CONFIG.CLIENT_ID,
        scope: GOOGLE_API_CONFIG.SCOPES,
        // Drive scopes cannot be granted alongside the YouTube scopes this
        // project already holds, so never merge previously granted scopes.
        include_granted_scopes: false,
        callback: async (tokenResponse) => {
          if (tokenResponse.error) {
            console.error(`Error getting OAuth token: ${tokenResponse.error}`);
            dispatch(setIsLoading(false));
            return;
          }
          
          // Create and render the picker
          const picker = new window.google.picker.PickerBuilder()
            .addView(
              new window.google.picker.DocsView()
                .setIncludeFolders(true)
                .setMimeTypes("video/mp4,video/quicktime,video/x-ms-wmv,video/x-msvideo,video/webm,video/x-matroska")
                .setSelectFolderEnabled(false)
            )
            .setOAuthToken(tokenResponse.access_token)
            .setDeveloperKey(GOOGLE_API_CONFIG.API_KEY)
            .setCallback(async (data) => {
              try {
                if (data[window.google.picker.Response.ACTION] === window.google.picker.Action.PICKED) {
                  const selectedDocs = data[window.google.picker.Response.DOCUMENTS];
                  
                  if (Array.isArray(selectedDocs) && selectedDocs.length > 0) {
                    const videoFiles = [];
                    
                    for (const doc of selectedDocs) {
                      try {
                        // Get file metadata
                        const accessToken = tokenResponse.access_token;
                        
                        // Get the actual file content
                        videoFiles.push({
                          id: doc.id,
                          name: doc.name,
                          mimeType: doc.mimeType,
                          size: doc.sizeBytes,
                          accessToken: accessToken
                        });
                      } catch (error) {
                        console.error(`Error processing file ${doc.name}:`, error);
                      }
                    }
                    
                    if (videoFiles.length > 0) {
                      onSelect(videoFiles);
                    }
                  }
                }
              } catch (error) {
                console.error("Error in Google Drive picker callback:", error);
              } finally {
                dispatch(setIsLoading(false));
              }
            })
            .build();
            
          picker.setVisible(true);
        }
      });
      
      // Request OAuth token
      tokenClient.requestAccessToken();
      
    } catch (error) {
      console.error("Error initializing Google Drive picker:", error);
      dispatch(setIsLoading(false));
    }
  };
  
  return { openPicker };
};

// Download file from Google Drive
export const downloadFileFromGoogleDrive = async (fileInfo) => {
  try {
    // Fetch the file content using the Google Drive API
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileInfo.id}?alt=media`,
      {
        headers: {
          'Authorization': `Bearer ${fileInfo.accessToken}`
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to download file from Google Drive: ${response.statusText}`);
    }
    
    const blob = await response.blob();
    
    // Create a File object from the blob
    return new File([blob], fileInfo.name, {
      type: fileInfo.mimeType,
      lastModified: new Date().getTime()
    });
  } catch (error) {
    console.error('Error downloading file from Google Drive:', error);
    throw error;
  }
};
